import { createContext, useContext, ReactNode, useMemo, useCallback, useRef } from 'react';

export type EventListener = (data?: any) => void;

interface EventBusContextProps {
  subscribe: (event: string, callback: EventListener) => () => void;
  publish: (event: string, data?: any) => void;
}

const EventBusContext = createContext<EventBusContextProps>({} as any);

const EventBusContextProvider = ({ children }: { children: ReactNode }) => {
  const listenersRef = useRef<Record<string, Set<EventListener>>>({});

  const subscribe = useCallback((event: string, callback: EventListener) => {
    if (!listenersRef.current[event]) {
      listenersRef.current[event] = new Set();
    }

    listenersRef.current[event].add(callback);

    return () => {
      listenersRef.current[event]?.delete(callback);
      if (listenersRef.current[event]?.size === 0) {
        delete listenersRef.current[event];
      }
    };
  }, []);

  const publish = useCallback((event: string, data?: any) => {
    const eventListeners = listenersRef.current[event];
    if (eventListeners) {
      eventListeners.forEach((callback) => callback(data));
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      subscribe,
      publish,
    }),
    [subscribe, publish],
  );

  return <EventBusContext.Provider value={contextValue}>{children}</EventBusContext.Provider>;
};

export const useAppEventBusContext = () => {
  return useContext(EventBusContext);
};

export default EventBusContextProvider;
